import { Builder } from '@builder.io/react';
import React from 'react';

import { ProgressiveImage } from '@videoblocks/storywind';

import BuilderSection from '../layouts/BuilderSection';

interface FeatureCard {
  image: string;
  imageAlt: string;
  title: string;
  body: string;
}

export interface FeaturesCardsProps {
  heading: string;
  bodyText: string;
  featureCards: FeatureCard[];
  backgroundColor?: string;
  cardBackgroundColor?: string;
  cardPadding?: string;
  enableMobileSwipe?: boolean;
}

export default function FeaturesCards({
  heading,
  bodyText,
  featureCards,
  backgroundColor,
  cardBackgroundColor,
  cardPadding,
  enableMobileSwipe = false,
}: FeaturesCardsProps) {
  const count = featureCards?.length || 0;

  const gridCols = {
    sm: {
      1: 'sm:grid-cols-1',
      2: 'sm:grid-cols-2',
    },
    lg: {
      1: 'lg:grid-cols-1',
      2: 'lg:grid-cols-2',
      3: 'lg:grid-cols-3',
    },
    xl: {
      1: 'xl:grid-cols-1',
      2: 'xl:grid-cols-2',
      3: 'xl:grid-cols-3',
      4: 'xl:grid-cols-4',
    },
  };

  const responsiveGridClasses = [
    gridCols.sm[Math.min(count, 2)],
    gridCols.lg[Math.min(count, 3)],
    gridCols.xl[Math.min(count, 4)],
  ].join(' ');

  const gridClasses = `grid grid-cols-1 ${responsiveGridClasses} gap-8`;

  const containerClasses = enableMobileSwipe
    ? `flex gap-8 overflow-x-auto pb-4 md:grid ${responsiveGridClasses} md:gap-8 md:overflow-visible`
    : gridClasses;

  return (
    <div style={{ backgroundColor }}>
      <BuilderSection>
        <div className="flex flex-col w-full">
          <div className="flex flex-col items-center justify-center gap-4 mb-12 text-center">
            <h2 className="font-polysans text-5xl font-bold m-0">{heading}</h2>
            {bodyText && (
              <div
                data-testid="body-text-container"
                className="text-xl font-normal text-gray-900 max-w-2xl mx-auto"
                dangerouslySetInnerHTML={{ __html: bodyText }}
              />
            )}
          </div>

          <div className={containerClasses}>
            {featureCards?.length > 0 &&
              featureCards.map((card, index) => (
                <div
                  key={`feature-card-${index}`}
                  className={`rounded-lg flex flex-col gap-6 ${
                    enableMobileSwipe ? 'flex-shrink-0 w-72 md:w-auto' : ''
                  }`}
                  style={{
                    backgroundColor: cardBackgroundColor,
                  }}
                >
                  {card.image && (
                    <div className="w-full aspect-square rounded-lg overflow-hidden bg-white">
                      <ProgressiveImage
                        src={card.image}
                        alt={card.imageAlt}
                        className="w-full h-full object-cover"
                        width="288"
                        height="288"
                        shouldLazyLoad
                      />
                    </div>
                  )}

                  <div
                    className={`flex flex-col gap-2 ${
                      card.image && cardPadding ? 'pt-0' : ''
                    }`}
                    style={{ padding: cardPadding }}
                  >
                    <h3 className="font-polysans text-2xl font-bold text-gray-900 m-0">
                      {card.title}
                    </h3>

                    <div
                      className="text-lg font-normal text-gray-900"
                      dangerouslySetInnerHTML={{ __html: card.body }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </BuilderSection>
    </div>
  );
}

Builder.registerComponent(FeaturesCards, {
  name: 'Features Cards',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'The Storyblocks difference',
    },
    {
      name: 'bodyText',
      friendlyName: 'Body Text',
      type: 'richText',
      defaultValue:
        'Optional body copy here: Using stock footage saves video producers time and money, and our library offers a nearly endless supply of content.',
      onChange: (options) => {
        const characterLimit = 300;
        const bodyText = options.get('bodyText');
        const htmlTagRegex = /<.*?>/gi;

        const textOnly = bodyText.replaceAll(htmlTagRegex, '');
        if (textOnly.length <= characterLimit) {
          return;
        }

        const tokenizedTags = [...bodyText.matchAll(htmlTagRegex)].map(
          (item) => item[0]
        );
        const tokenizedText = bodyText.split(htmlTagRegex);

        const trimmedTextNodes = tokenizedText.reduce((textNodes, text) => {
          const currentTextLength = textNodes.join('').length;
          return currentTextLength + text.length <= characterLimit
            ? textNodes.concat(text)
            : textNodes.concat(
                text.slice(0, characterLimit - currentTextLength)
              );
        }, []);

        const trimmedRichText = trimmedTextNodes.reduce(
          (acc, node, index) => acc + node + (tokenizedTags[index] || ''),
          ''
        );

        alert(`Max of ${characterLimit} characters allowed.`);
        options.set('bodyText', trimmedRichText);
      },
    },
    {
      name: 'backgroundColor',
      friendlyName: 'Background Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Select a custom background color for the section',
    },
    {
      name: 'cardBackgroundColor',
      friendlyName: 'Card Background Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Select a custom background color for the cards',
    },
    {
      name: 'cardPadding',
      friendlyName: 'Card Padding',
      type: 'string',
      defaultValue: '',
      helperText:
        'Set a custom padding for the cards (e.g., "24px", "16px 24px").',
      regex: {
        pattern:
          '^\\s*((0|\\d+(?:\\.\\d+)?)(px|em|rem|%|vw|vh|ch|ex|vmin|vmax|cm|mm|in|pt|pc|Q|lh|rlh|vi|vb|ic)\\s*){1,4}$',
        options: 'i',
        message:
          'Invalid padding format. Please use 1 to 4 values with units (e.g., "24px", "16px 24px").',
      },
    },
    {
      name: 'enableMobileSwipe',
      friendlyName: 'Enable Mobile Swipe',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Allow users to swipe horizontally through cards on mobile devices',
    },
    {
      name: 'featureCards',
      friendlyName: 'Feature Cards',
      type: 'list',
      required: true,
      helperText:
        'A list of feature cards highlighting key differentiators. Each card includes an image, title, and description.',
      defaultValue: [
        {
          image: '',
          imageAlt: 'High quality diverse stock library',
          title: 'High-quality, diverse stock library',
          body: 'Our library is expertly curated to provide the in-demand content you need to make your creative stand out.',
        },
        {
          image: '',
          imageAlt: 'Premium quality assets',
          title: 'Premium Quality Assets',
          body: 'Access professionally produced content that meets the highest standards of quality and creativity.',
        },
        {
          image: '',
          imageAlt: 'Unlimited downloads',
          title: 'Unlimited Downloads',
          body: 'Download as many assets as you need without worrying about additional costs or limitations.',
        },
        {
          image: '',
          imageAlt: 'Commercial license',
          title: 'Commercial License',
          body: 'Use our content for any commercial project with our comprehensive licensing agreement.',
        },
      ],
      onChange: (options) => {
        const maxAllowed = 8;

        if (options.get('featureCards').length > maxAllowed) {
          options.set(
            'featureCards',
            options.get('featureCards').slice(0, maxAllowed)
          );
          alert(
            `Maximum number of feature cards allowed is ${maxAllowed}. Modify an existing feature card instead.`
          );
        }
      },
      subFields: [
        {
          name: 'image',
          friendlyName: 'Image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
          required: false,
          helperText: 'Upload an image for the feature card',
        },
        {
          name: 'imageAlt',
          friendlyName: 'Image Alt Text',
          type: 'string',
          required: true,
          helperText: 'Descriptive text for screen readers and accessibility',
        },
        {
          name: 'title',
          friendlyName: 'Card Title',
          type: 'string',
          required: true,
        },
        {
          name: 'body',
          friendlyName: 'Card Body',
          type: 'richText',
          required: true,
          helperText: 'Description text for the feature card',
        },
      ],
    },
    {
      name: 'Identifier',
      type: 'string',
      enum: ['Stockblocks'],
      defaultValue: 'Stockblocks',
      advanced: true,
      onChange: (_, context) => {
        context.set('Identifier', 'Stockblocks');
      },
      helperText: "Identifier for the repo, don't change this",
    },
  ],
});
